import * as React from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { DownloadOutlined, FilterOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Select, Button, Drawer, Switch, Input } from 'antd';
import moment from 'moment-timezone';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import {
	fetchSupplierFacilitiesIfEverInPrivateNetworkForBuyer,
	fetchSupplierFacilitiesIfEverInPrivateNetworkForSupplier,
	supplierFacilitiesRestCrudThunksForBuyer,
	supplierFacilitiesRestCrudThunksForSupplier,
} from '../../thunks/supplier_facilities_thunks';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import BuyerRegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';

import {
	decodeUTCAdjustedDate,
	getDefaultDateRanges,
	stringArrToList,
} from '../../utils/DataFormatterUtils';
import {
	ANALYTICS_DEFAULT_TIME_ZONE,
	FILTER_CONFIG_NAMES,
	FILTER_FIELD_TYPE,
	ROLE_TYPES,
} from '../../utils/DataConstants';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesHeirarchicalForSupplier,
} from '../../thunks/problem_types_thunks';
import { BUYER_ADMIN_ONLY, hasRequiredRole } from '../roles/roles';
import AnalyticsSelectedFilters from '../analytics_control_bar/AnalyticsSelectedFilters';
import {
	assetTypesRestCrudThunksForBuyer,
	assetTypesRestCrudThunksForSupplier,
} from '../../thunks/asset_types_thunks';
import OWAsyncTreeSelect from '../ow_async_tree_select/OWAsyncTreeSelect';
import OWButton from '../ow_button/OWButton';
import OWDatePickerWithTimeZone from '../ow_date_picker_with_time_zone/OWDatePickerWithTimeZone';

interface DowntimeAnalyticsControlBarFormProps extends FormComponentProps {
	startDate: any;
	endDate: any;
	timeZone: any;

	locationIds: any;

	fetchMultipleLocations: any;
	fetchLocations: any;

	hideLocationsControl: any;
	locations: any;
	showSearch?: boolean;
	showDownload: boolean;
	onSearch?: any;
	search?: any;
	filterConfig: any;
	onDownload: any;
	currentUser: any;
	dateRangeDefaultLabel: any;
	updateFilters: any;
	assetTypes: any;
	fetchAssetTypes: any;
	assetTypeIds: any;
	filters: any;
}

class DowntimeAnalyticsControlBar extends React.Component<
	DowntimeAnalyticsControlBarFormProps,
	any
> {
	state = {
		editing: false,
		allowLocationEdit: false,
		allowSupplierEdit: false,
	};

	getFilterValues = () => {
		const { startDate, endDate, filters } = this.props;
		const timeZone = this.props.timeZone || ANALYTICS_DEFAULT_TIME_ZONE;
		const { locationIds, assetTypeIds, manufacturer } = filters;
		return {
			startDate: startDate || undefined,
			endDate: endDate || undefined,
			timeZone,
			locationIds,
			assetTypeIds,
			manufacturer,
		};
	};

	componentDidMount() {
		const { startDate, endDate, updateFilters } = this.props;

		if (!startDate || !endDate) {
			updateFilters(this.getFilterValues());
		}
	}

	handleSubmit = (e) => {
		const { updateFilters } = this.props;
		e.preventDefault();
		this.props.form.validateFields((err, values) => {
			if (!err) {
				const timeZone = nullSafeGet('timeZone', values) || ANALYTICS_DEFAULT_TIME_ZONE;
				const start = nullSafeGet('startAndEndDates.0', values);
				const end = nullSafeGet('startAndEndDates.1', values);
				// Keep the picked wall-clock time, reinterpreted in the selected zone. The API params are
				// encoded once at the page boundary, so these stay moments here.
				const startDate = start ? moment(start).tz(timeZone, true) : undefined;
				const endDate = end ? moment(end).tz(timeZone, true) : undefined;

				const newFilters = {
					locationIds: values.selectAllLocations
						? undefined
						: values.locationIds && values.locationIds.sort(),
					assetTypeIds: values.assetTypeIds,
					manufacturer: values.manufacturer,
					startDate,
					endDate,
					timeZone,
				};
				updateFilters(newFilters);
				this.setState({ editing: false });
			}
		});
	};

	handleCancel = (e) => {
		e.preventDefault();
		this.props.form.resetFields();
		this.setState({ editing: false });
	};

	updateFiltersFromDisplay = (updatedFilter) => {
		const { updateFilters } = this.props;
		updateFilters({ ...this.getFilterValues(), ...updatedFilter });
	};

	getLocationsString(locationIds = [], locations) {
		const locationNames = locationIds.map((id) => nullSafeGet(`${id}.name`, locations.records));
		if (locationIds.length === 0) {
			return 'no locations';
		} else if (locationIds.length > 0 && locationIds.length < 3) {
			return stringArrToList(locationNames);
		} else {
			return `${locationIds.length} locations`;
		}
	}

	getSupplierFacilitiesString(supplierFacilityIds = [], supplierFacilities) {
		const supplierFacilityNames = supplierFacilityIds.map((id) =>
			nullSafeGet(`${id}.displayName`, supplierFacilities.records)
		);
		if (supplierFacilityIds.length === 0) {
			return 'no suppliers';
		} else if (supplierFacilityIds.length > 0 && supplierFacilityIds.length < 3) {
			return stringArrToList(supplierFacilityNames);
		} else {
			return `${supplierFacilityIds.length} suppliers`;
		}
	}

	handleAllLocationsChange = (value) => {
		this.setState({ allowLocationEdit: !value });
	};

	handleAllSuppliersChange = (value) => {
		this.setState({ allowSupplierEdit: !value });
	};

	openDrawer = () => this.setState({ editing: true });

	render() {
		const {
			startDate,
			endDate,

			form,
			hideLocationsControl,
			locations,
			showSearch,
			showDownload,
			onSearch,
			search,
			filterConfig,
			onDownload,
			currentUser,
			dateRangeDefaultLabel,
			assetTypes,
			fetchAssetTypes,
			filters,
		} = this.props;
		const { locationIds, assetTypeIds, manufacturer } = filters;
		const activeTimeZone = this.props.timeZone || ANALYTICS_DEFAULT_TIME_ZONE;
		const { editing } = this.state;
		const RANGE_PICKER_DATE_FORMAT = 'MM-DD-YYYY';

		const dateRangeLabel = dateRangeDefaultLabel || 'Downtime Date Range';

		const isAdmin = hasRequiredRole(currentUser.roles, BUYER_ADMIN_ONLY);
		const hasAccessToAllLocations = nullSafeGet('hasAccessToAllLocations', currentUser) || isAdmin;
		const FilterControls = (
			<Form
				layout="vertical"
				hideRequiredMark={true}
				style={{ maxWidth: 800 }}
				onSubmit={this.handleSubmit}
			>
				<h5>Filters</h5>
				<Form.Item label={dateRangeLabel}>
					<OWDatePickerWithTimeZone
						key={
							startDate && endDate
								? moment(startDate).format('YYMMDDHHmm') + moment(endDate).format('YYMMDDHHmm')
								: undefined
						}
						form={form}
						paramName="startAndEndDates"
						timeZoneParamName="timeZone"
						ranges={getDefaultDateRanges(activeTimeZone)}
						format={RANGE_PICKER_DATE_FORMAT}
						showTime={false}
						hideTimezonesWithUtcDefault
						initialStartDate={decodeUTCAdjustedDate(startDate, false, activeTimeZone)}
						initialEndDate={decodeUTCAdjustedDate(endDate, true, activeTimeZone)}
						initialTimeZone={activeTimeZone}
					/>
				</Form.Item>
				{hideLocationsControl ? null : (
					<>
						<Form.Item style={{ marginBottom: '0px' }} label="Locations">
							{form.getFieldDecorator('selectAllLocations', {
								initialValue: !this.state.allowLocationEdit,
								valuePropName: 'checked',
							})(<Switch onChange={this.handleAllLocationsChange} />)}
							<label style={{ marginLeft: 8 }}>
								Select {hasAccessToAllLocations ? 'All Locations' : 'All Assigned Locations'}
							</label>
						</Form.Item>
						<Form.Item>
							{form.getFieldDecorator('locationIds', {
								initialValue: this.state.allowLocationEdit ? locationIds : [],
							})(
								<BuyerRegionsLocationsTreeSelect
									mode="multiple"
									disabled={!this.state.allowLocationEdit}
									filtersSlice={locations}
									filtersValueAccessor={() => form.getFieldValue('locationIds')}
									targetCollectionName={'locationsAnalyticsDropdown'}
								/>
							)}
						</Form.Item>
					</>
				)}
				{nullSafeGet('assetTypes', filterConfig) ? (
					<Form.Item label="Asset Types">
						{form.getFieldDecorator('assetTypeIds', {
							initialValue: assetTypeIds,
						})(
							<OWAsyncTreeSelect
								allowClear={true}
								mode="multiple"
								stateSlice={assetTypes}
								targetCollectionName="analyticsDropdown"
								fetchData={(
									searchText,
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								) =>
									fetchAssetTypes(
										{ name: searchText },
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									)
								}
								fetchMultiple={(ids, targetCollectionName) => {}}
								renderRecord={(sf) => (
									<Select.Option key={sf.id} value={sf.id}>
										{sf.name}
									</Select.Option>
								)}
							/>
						)}
					</Form.Item>
				) : null}
				{nullSafeGet('manufacturer', filterConfig) ? (
					<Form.Item label="Manufacturer">
						{form.getFieldDecorator('manufacturer', {
							initialValue: manufacturer,
						})(<Input.Search placeholder="Search for a manufacturer" />)}
					</Form.Item>
				) : null}
				<Form.Item>
					<OWButton type="primary" htmlType="submit">
						Update filters
					</OWButton>
					<Button onClick={this.handleCancel} style={{ marginLeft: 16 }}>
						Cancel
					</Button>
				</Form.Item>
			</Form>
		);

		return (
			<div
				style={{
					width: '100%',
				}}
			>
				<div className="flex flex-row items-center justify-between">
					<div className="flex flex-row items-center">
						{showSearch && (
							<Input.Search
								placeholder={'Search Problem type'}
								value={search}
								style={{ width: 180 }}
								className="mr-2"
								onChange={onSearch}
							/>
						)}
						<Button
							size="large"
							icon={<FilterOutlined translate="" />}
							onClick={() => this.setState({ editing: !this.state.editing })}
						>
							Filters
						</Button>
					</div>
					{showDownload && (
						<Button
							size="large"
							icon={<DownloadOutlined translate="" />}
							className="inline-block"
							onClick={onDownload}
						>
							<span className="inline-block">Export CSV</span>
						</Button>
					)}
				</div>
				<AnalyticsSelectedFilters
					filterValues={this.getFilterValues()}
					filterConfig={[
						...(hideLocationsControl
							? []
							: [
									{
										label: hasAccessToAllLocations ? 'Location' : 'Assigned Location',
										fieldName: 'locationIds',
										stateSlice: locations,
										valueAccessor: (loc) => loc.id,
										renderItem: (loc) => loc.name,
										pillMandatory: true,
									},
							  ]),
						{
							label: dateRangeLabel,
							fieldName: 'default',
							type: FILTER_FIELD_TYPE.DATE_RANGE,
							// mandatory: true,
							renderRecord: (item) => item,
							valueAccessor: (item) => item,
						},
						{
							label: 'Asset Type',
							fieldName: FILTER_CONFIG_NAMES.ASSET_TYPE,
							stateSlice: assetTypes,
							valueAccessor: (pt) => pt.id,
							renderItem: (pt) => pt.name,
						},
						// {
						// 	label: 'Manufacturers',
						// 	fieldName: 'manufacturers',
						// 	stateSlice: manufacturers || [],
						// 	valueAccessor: (pt) => pt.name,
						// 	renderItem: (pt) => pt.name,
						// },
					]}
					updateFilters={this.updateFiltersFromDisplay}
					openFilters={this.openDrawer}
				/>
				<Drawer closable={false} width={800} visible={editing}>
					{FilterControls}
				</Drawer>
			</div>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,

	currentUser: state.session.currentUser,
	locations: state.locations,
	supplierFacilities: state.supplier_facilities,
	updateFilters: ownProps.updateFilters,
	clearFilters: ownProps.clearFilters,
	startDate: ownProps.startDate,
	endDate: ownProps.endDate,
	locationIds: ownProps.locationIds,
	supplierFacilityIds: ownProps.supplierFacilityIds,
	problemTypes: state.problem_types,
	assetTypes: state.asset_types,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: locationsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchMultipleSupplierFacilities: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierFacilitiesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: supplierFacilitiesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchSupplierFacilitiesIfEverInPrivateNetwork: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchSupplierFacilitiesIfEverInPrivateNetworkForSupplier(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: fetchSupplierFacilitiesIfEverInPrivateNetworkForBuyer(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		return dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesHeirarchicalForSupplier(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: problemTypesHeirarchicalForBuyer(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		);
	},
	fetchAssetTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		return dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier.readLite(
						{ ...params, no_pagination: true },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: assetTypesRestCrudThunksForBuyer.readLite(
						{ ...params, no_pagination: true },
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		);
	},
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(DowntimeAnalyticsControlBar)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(Form.create()(ComponentWithoutUserType));
